import React, { useEffect, useState } from 'react';
import { Eye, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DateField } from './DateField';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { recuentosAPI, ESTADOS, formatFechaHora } from '@/lib/recuentos';

const Filtros = ({ f, setF, lugares }) => (
  <div className="flex flex-wrap gap-2 items-end">
    <Select value={f.lugar} onValueChange={(v) => setF({ ...f, lugar: v })}>
      <SelectTrigger className="w-56" data-testid="historial-filtro-lugar"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Todos los lugares</SelectItem>
        {lugares.map((l) => <SelectItem key={l.codigo} value={l.codigo}>{l.local} - {l.nombre_bodega_justo}</SelectItem>)}
      </SelectContent>
    </Select>
    <Select value={f.estado} onValueChange={(v) => setF({ ...f, estado: v })}>
      <SelectTrigger className="w-44" data-testid="historial-filtro-estado"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Todos los estados</SelectItem>
        {Object.entries(ESTADOS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
      </SelectContent>
    </Select>
    <DateField value={f.desde} onChange={(v) => setF({ ...f, desde: v })} placeholder="Desde" testId="historial-filtro-desde" />
    <DateField value={f.hasta} onChange={(v) => setF({ ...f, hasta: v })} placeholder="Hasta" testId="historial-filtro-hasta" />
  </div>
);

export const HistorialTab = ({ lugares, onOpen, refreshKey }) => {
  const [f, setF] = useState({ lugar: 'all', estado: 'all', desde: '', hasta: '' });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (f.lugar !== 'all') params.lugar_codigo = f.lugar;
      if (f.estado !== 'all') params.estado = f.estado;
      if (f.desde) params.fecha_desde = f.desde;
      if (f.hasta) params.fecha_hasta = f.hasta;
      const { data } = await recuentosAPI.list(params);
      setRows(data);
    } catch {
      toast.error('Error al cargar recuentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [f, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-between gap-2 flex-wrap">
        <Filtros f={f} setF={setF} lugares={lugares} />
        <Button variant="outline" onClick={load} disabled={loading} data-testid="historial-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
      </div>
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm" data-testid="historial-table">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-4 py-3 text-left">Lugar</th>
              <th className="px-4 py-3 text-left">Fecha / hora</th>
              <th className="px-4 py-3 text-left">Responsable</th>
              <th className="px-4 py-3 text-left">Estado</th>
              <th className="px-4 py-3 text-center">Contados</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Sin recuentos</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t hover:bg-orange-50/40 cursor-pointer" onClick={() => onOpen(r.id)} data-testid={`historial-row-${r.id}`}>
                <td className="px-4 py-3 font-medium text-slate-800">
                  {r.lugar_nombre}
                  <span className={`ml-2 text-xs font-semibold px-2 py-0.5 rounded-full ${r.origen === 'stock_tupu' ? 'bg-violet-100 text-violet-800' : 'bg-slate-200 text-slate-700'}`} data-testid={`historial-origen-${r.id}`}>
                    {r.origen === 'stock_tupu' ? 'Stock TUPU' : 'Conteo físico'}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-600">{formatFechaHora(r.created_at)}</td>
                <td className="px-4 py-3 text-slate-600">{r.responsable || r.user_name}</td>
                <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ESTADOS[r.estado]?.className}`}>{ESTADOS[r.estado]?.label}</span></td>
                <td className="px-4 py-3 text-center">{r.ingredientes_contados}/{r.total_ingredientes}</td>
                <td className="px-4 py-3 text-right"><Eye className="w-4 h-4 text-slate-400 inline" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
